import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Alert,
  Chip,
  MenuItem,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Avatar,
  Skeleton,
  IconButton,
  Fab,
  Tooltip,
  Dialog,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  DirectionsCar as CarIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  PlayArrow as PlayArrowIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Notes as NotesIcon,
  Close as CloseIcon,
  Directions as DirectionsIcon,
  Draw as DrawIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import SignatureCanvas from 'react-signature-canvas';
import { generaRapportinoPDF } from '../utils/pdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import {
  interventiService,
  clientiService,
  cantieriService,
  usersService,
  rapportiniService,
  timbratureService,
  veicoliService,
} from '../services/api';

// Componente StatBox riutilizzabile
const StatBox = ({ icon, value, label }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      mb: 0.5
    }}>
      {icon && React.cloneElement(icon, { sx: { color: 'rgba(255,255,255,0.7)', fontSize: '1.2rem' } })}
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
      {label}
    </Typography>
  </Box>
);

// Componente FormSection riutilizzabile
const FormSection = ({ icon, title, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      height: '100%',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
        {icon}
      </Avatar>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

// Componente TabPanel
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const InterventoDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editingTecnici, setEditingTecnici] = useState(false); // punto 2: editor solo-tecnici per il capocantiere
  const [tecniciEdit, setTecniciEdit] = useState([]);
  const [tecniciError, setTecniciError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentTab, setCurrentTab] = useState(0);

  const [intervento, setIntervento] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [capocantieri, setCapocantieri] = useState([]);
  const [tecnici, setTecnici] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [rapportino, setRapportino] = useState(null);
  const [needsRapportino, setNeedsRapportino] = useState(false);
  const [veicoliDisponibili, setVeicoliDisponibili] = useState([]);

  // Firma posticipata dialog
  const [firmaDialogOpen, setFirmaDialogOpen] = useState(false);
  const [firmaNome, setFirmaNome] = useState('');
  const [firmaLoading, setFirmaLoading] = useState(false);
  const [firmaError, setFirmaError] = useState('');
  const firmaSigCanvas = useRef(null);

  const [formData, setFormData] = useState({
    tipologia: 'manutenzione',
    cliente_id: '',
    cantiere_id: '',
    capocantiere_id: '',
    data: null,
    ora_inizio: null,
    ora_fine: null,
    descrizione_lavori: '',
    note_operative: '',
    targa_mezzo: '',
    stato: 'programmato',
    tecnici_ids: [],
    abilita_timbrature_viaggio: true,
  });

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (currentTab === 1 && intervento?.cantiere_id && tasks.length === 0) {
      loadTasks();
    }
  }, [currentTab, intervento]);

  useEffect(() => {
    if (intervento) {
      checkRapportino();
    }
  }, [intervento]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [interventoRes, clientiRes, cantieriRes, capocRes, tecnRes] = await Promise.all([
        interventiService.getById(id),
        clientiService.getAll(),
        cantieriService.getAll(),
        usersService.getCapocantieri(),
        usersService.getTecnici(),
      ]);

      setIntervento(interventoRes.data);
      setClienti(clientiRes.data);
      setCantieri(cantieriRes.data);
      setCapocantieri(capocRes.data);
      setTecnici(tecnRes.data);

      // Carica veicoli disponibili per la data dell'intervento
      if (interventoRes.data.data) {
        try {
          const veicoliRes = await veicoliService.getDisponibili(interventoRes.data.data, id);
          setVeicoliDisponibili(veicoliRes.data);
        } catch (e) {
          console.error('Errore caricamento veicoli:', e);
        }
      }

      setFormData({
        tipologia: interventoRes.data.tipologia,
        cliente_id: interventoRes.data.cliente_id,
        cantiere_id: interventoRes.data.cantiere_id,
        capocantiere_id: interventoRes.data.capocantiere_id,
        data: interventoRes.data.data ? dayjs(interventoRes.data.data) : null,
        ora_inizio: interventoRes.data.ora_inizio
          ? dayjs(`2000-01-01T${interventoRes.data.ora_inizio}`)
          : null,
        ora_fine: interventoRes.data.ora_fine
          ? dayjs(`2000-01-01T${interventoRes.data.ora_fine}`)
          : null,
        descrizione_lavori: interventoRes.data.descrizione_lavori,
        note_operative: interventoRes.data.note_operative || '',
        targa_mezzo: interventoRes.data.targa_mezzo || '',
        stato: interventoRes.data.stato,
        tecnici_ids: interventoRes.data.tecnici
          ? interventoRes.data.tecnici.map((t) => t.id)
          : [],
        abilita_timbrature_viaggio: interventoRes.data.abilita_timbrature_viaggio !== false,
      });
    } catch (error) {
      setError('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async () => {
    if (!intervento?.cantiere_id) return;

    try {
      setLoadingTasks(true);
      const response = await cantieriService.getTasks(intervento.cantiere_id);
      setTasks(Array.isArray(response.data.tasks) ? response.data.tasks : []);
    } catch (error) {
      console.error('Errore nel caricamento tasks:', error);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange = (field) => async (value) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    // Ricarica veicoli disponibili quando cambia la data
    if (field === 'data' && value) {
      try {
        const dataFormatted = value.format('YYYY-MM-DD');
        const veicoliRes = await veicoliService.getDisponibili(dataFormatted, id);
        setVeicoliDisponibili(veicoliRes.data);
      } catch (e) {
        console.error('Errore caricamento veicoli:', e);
      }
    }
  };

  const handleCantiereChange = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        cantiere_id: newValue.id,
        cliente_id: newValue.cliente_id,
      });
    } else {
      setFormData({
        ...formData,
        cantiere_id: '',
        cliente_id: '',
      });
    }
  };

  const handleSave = async () => {
    try {
      const dataToSend = {
        ...formData,
        data: formData.data ? formData.data.format('YYYY-MM-DD') : null,
        ora_inizio: formData.ora_inizio ? formData.ora_inizio.format('HH:mm:ss') : null,
        ora_fine: formData.ora_fine ? formData.ora_fine.format('HH:mm:ss') : null,
      };

      await interventiService.update(id, dataToSend);
      setSuccess('Intervento aggiornato con successo');
      setEditing(false);
      loadData();

      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  // Punto 2: il capocantiere modifica SOLO i tecnici del proprio intervento.
  const handleEditTecnici = () => {
    setTecniciEdit(intervento?.tecnici ? intervento.tecnici.map((t) => t.id) : []);
    setTecniciError('');
    setEditingTecnici(true);
  };

  const handleCancelTecnici = () => {
    setEditingTecnici(false);
    setTecniciError('');
  };

  const handleSaveTecnici = async () => {
    try {
      await interventiService.updateTecnici(id, { tecnici_ids: tecniciEdit });
      setSuccess('Tecnici aggiornati con successo');
      setEditingTecnici(false);
      setTecniciError('');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setTecniciError(error.response?.data?.message || 'Errore nell\'aggiornamento dei tecnici');
    }
  };

  const handleGestisciTimbrature = () => {
    navigate('/timbrature', {
      state: {
        interventoId: id
      }
    });
  };

  // Duplica: porta alla pagina Interventi che apre il dialog già in modalità Duplica
  // (riusa la stessa logica di duplicazione/ricorrenza usata dal calendario).
  const handleDuplicaIntervento = () => {
    if (!intervento) return;
    navigate('/interventi', {
      state: { duplicateInterventoId: intervento.id }
    });
  };

  const handleOpenFirmaDialog = () => {
    setFirmaNome('');
    setFirmaDialogOpen(true);
  };

  const handleCloseFirmaDialog = () => {
    setFirmaDialogOpen(false);
    setFirmaNome('');
    setFirmaError('');
    if (firmaSigCanvas.current) {
      firmaSigCanvas.current.clear();
    }
  };

  const handleSaveFirma = async () => {
    if (!firmaSigCanvas.current || firmaSigCanvas.current.isEmpty()) {
      setFirmaError('Disegna la firma nel riquadro prima di salvare.');
      return;
    }
    if (!firmaNome.trim()) {
      setFirmaError('Inserisci il nome del firmatario.');
      return;
    }
    setFirmaError('');
    try {
      setFirmaLoading(true);
      const firmaData = {
        firma_cliente: firmaSigCanvas.current.toDataURL('image/png'),
        nome_firmatario: firmaNome,
      };
      const response = await rapportiniService.update(rapportino.id, firmaData);
      setRapportino(response.data);
      handleCloseFirmaDialog();
    } catch (err) {
      console.error('Errore nel salvataggio firma:', err);
      if (err.isOffline && err.queued) {
        // Firma salvata nella coda offline: chiudi (il toast globale avvisa, sincronizza dopo)
        handleCloseFirmaDialog();
        return;
      }
      setFirmaError(err.isOffline ? err.message : (err.response?.data?.message || 'Errore nel salvataggio della firma. Riprova.'));
    } finally {
      setFirmaLoading(false);
    }
  };

  const handleGetDirections = () => {
    const sedeOrigin = '45.4761692,8.63074';
    const destination = encodeURIComponent(intervento.cantiere_indirizzo || intervento.cantiere);
    const url = `https://www.google.com/maps/dir/?api=1&origin=${sedeOrigin}&destination=${destination}`;
    window.open(url, '_blank');
  };

  const checkRapportino = async () => {
    try {
      const [rapportinoRes, timbratureRes] = await Promise.all([
        rapportiniService.getByIntervento(id),
        timbratureService.getByIntervento(id),
      ]);

      setRapportino(rapportinoRes.data);

      const timbratureArray = Array.isArray(timbratureRes.data) ? timbratureRes.data : [];
      const hasFinelavoro = timbratureArray.some(t => t.tipo === 'fine_lavoro');
      const hasRapportino = rapportinoRes.data !== null && rapportinoRes.data !== undefined;

      setNeedsRapportino(hasFinelavoro && !hasRapportino);
    } catch (error) {
      console.error('Errore nel controllo rapportino:', error);
    }
  };

  const getStatoStyle = (stato) => {
    const styles = {
      programmato: { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196f3', border: '#2196f3', icon: <CalendarIcon /> },
      in_corso: { bg: 'rgba(255, 152, 0, 0.1)', color: '#ff9800', border: '#ff9800', icon: <PlayArrowIcon /> },
      completato: { bg: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', border: '#4caf50', icon: <CheckCircleIcon /> },
    };
    return styles[stato] || { bg: 'rgba(158, 158, 158, 0.1)', color: '#9e9e9e', border: '#9e9e9e', icon: null };
  };

  const getTipologiaStyle = (tipologia) => {
    return tipologia === 'manutenzione'
      ? { bg: 'rgba(229, 0, 0, 0.1)', color: '#e50000' }
      : { bg: 'rgba(156, 39, 176, 0.1)', color: '#9c27b0' };
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'amministratore';
  const canManage = isAdmin || user?.role === 'capocantiere';
  // Punto 2: il capocantiere ASSEGNATO a questo intervento può modificarne i tecnici
  const isCapoDiQuesto = user?.role === 'capocantiere' && !!intervento && intervento.capocantiere_id === user?.id;
  // #5: l'amministratore NON compila i rapportini (solo capocantiere e admin/super_admin)
  const canCompilare = user?.role === 'capocantiere' || user?.role === 'admin' || user?.role === 'super_admin';

  // Statistiche tasks
  const tasksStats = {
    totali: Array.isArray(tasks) ? tasks.length : 0,
    completate: Array.isArray(tasks) ? tasks.filter(t => t.completata === true).length : 0,
    inCorso: Array.isArray(tasks) ? tasks.filter(t => t.completata !== true).length : 0,
  };
  const progressoTasks = tasksStats.totali > 0 ? Math.round((tasksStats.completate / tasksStats.totali) * 100) : 0;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton variant="rounded" height={250} sx={{ mb: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!intervento) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <WarningIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Intervento non trovato
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/interventi')}
            sx={{ mt: 2 }}
          >
            Torna agli Interventi
          </Button>
        </Paper>
      </Container>
    );
  }

  const statoStyle = getStatoStyle(intervento.stato);
  const tipologiaStyle = getTipologiaStyle(intervento.tipologia);

  return (
    <Box sx={{ pb: { xs: 10, sm: 4 } }}>
      {/* Header moderno con sfondo scuro */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          color: 'white',
          pt: { xs: 2, sm: 3 },
          pb: { xs: 4, sm: 5 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background: 'radial-gradient(circle, rgba(229,0,0,0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Breadcrumb / Back */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/interventi')}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              mb: 2,
              '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Torna agli Interventi
          </Button>

          {/* Titolo e info */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: { xs: 56, sm: 64 },
                  height: { xs: 56, sm: 64 },
                  bgcolor: tipologiaStyle.bg,
                  color: tipologiaStyle.color,
                  border: '3px solid rgba(255,255,255,0.2)',
                }}
              >
                <BuildIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                      letterSpacing: '-0.5px',
                    }}
                  >
                    {intervento.cantiere}
                  </Typography>
                  <Chip
                    icon={React.cloneElement(statoStyle.icon, { sx: { fontSize: '1rem !important' } })}
                    label={intervento.stato.replace('_', ' ')}
                    size="small"
                    sx={{
                      bgcolor: statoStyle.bg,
                      color: statoStyle.color,
                      border: `1px solid ${statoStyle.border}`,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      '& .MuiChip-icon': { color: statoStyle.color },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BusinessIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {intervento.cliente}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {dayjs(intervento.data).format('DD/MM/YYYY')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {intervento.ora_inizio} - {intervento.ora_fine}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Azioni header */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Indicazioni stradali">
                <IconButton
                  onClick={handleGetDirections}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  <DirectionsIcon />
                </IconButton>
              </Tooltip>

              {canManage && (
                <Button
                  variant="contained"
                  startIcon={!isMobile && <AccessTimeIcon />}
                  onClick={handleGestisciTimbrature}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                  }}
                >
                  {isMobile ? <AccessTimeIcon /> : 'Timbrature'}
                </Button>
              )}

              {isAdmin && !isMobile && (
                editing ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      onClick={() => setEditing(false)}
                      sx={{ color: 'white' }}
                    >
                      Annulla
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      sx={{
                        bgcolor: '#e50000',
                        '&:hover': { bgcolor: '#c50000' },
                      }}
                    >
                      Salva
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<ContentCopyIcon />}
                      onClick={handleDuplicaIntervento}
                      variant="outlined"
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.6)',
                        '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.12)' },
                      }}
                    >
                      Duplica
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => setEditing(true)}
                      sx={{
                        bgcolor: '#e50000',
                        '&:hover': { bgcolor: '#c50000' },
                      }}
                    >
                      Modifica
                    </Button>
                  </Box>
                )
              )}
            </Box>
          </Box>

          {/* Statistiche */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              gap: { xs: 2, sm: 3 },
              mt: 4,
            }}
          >
            <StatBox
              icon={<BuildIcon />}
              value={intervento.tipologia === 'manutenzione' ? 'MAN' : 'IMP'}
              label="Tipologia"
            />
            <StatBox
              icon={<PersonIcon />}
              value={intervento.tecnici?.length || 0}
              label="Tecnici"
            />
            <StatBox
              icon={<TimerIcon />}
              value={intervento.tempo_viaggio_andata || '-'}
              label="Min. Viaggio"
            />
            <StatBox
              icon={<CarIcon />}
              value={intervento.targa_mezzo || '-'}
              label="Veicolo"
            />
          </Box>
        </Container>
      </Box>

      {/* Contenuto principale */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
        {/* Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        {/* Stato Rapportino */}
        {canManage && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 3,
              background: rapportino
                ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.15) 100%)'
                : needsRapportino
                  ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 152, 0, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(158, 158, 158, 0.05) 0%, rgba(158, 158, 158, 0.1) 100%)',
              border: '2px solid',
              borderColor: rapportino ? '#4caf50' : needsRapportino ? '#ff9800' : 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: rapportino || !rapportino ? 2 : 0 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: rapportino
                    ? 'rgba(76, 175, 80, 0.15)'
                    : needsRapportino
                      ? 'rgba(255, 152, 0, 0.15)'
                      : 'rgba(158, 158, 158, 0.15)',
                }}
              >
                {rapportino ? (
                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                ) : needsRapportino ? (
                  <WarningIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                ) : (
                  <DescriptionIcon sx={{ color: '#9e9e9e', fontSize: 28 }} />
                )}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: rapportino ? '#2e7d32' : needsRapportino ? '#e65100' : '#424242',
                  }}
                >
                  {rapportino
                    ? 'Rapportino Compilato'
                    : needsRapportino
                      ? 'Rapportino NON Compilato'
                      : 'Rapportino Non Necessario'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {rapportino
                    ? `Compilato il ${rapportino.data ? dayjs(rapportino.data).format('DD/MM/YYYY') : 'N/A'}`
                    : needsRapportino
                      ? 'Il lavoro è terminato. È necessario compilare il rapportino.'
                      : 'Sarà necessario dopo la timbratura di fine lavoro.'}
                </Typography>
              </Box>
              {!rapportino && canCompilare && (
                <Button
                  variant="contained"
                  startIcon={<DescriptionIcon />}
                  onClick={handleGestisciTimbrature}
                  sx={{
                    bgcolor: needsRapportino ? '#ff9800' : '#1976d2',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: needsRapportino ? '#f57c00' : '#1565c0',
                    },
                  }}
                >
                  {needsRapportino ? 'Compila Ora' : 'Vai a Timbrature'}
                </Button>
              )}
            </Box>

            {rapportino && (
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', pt: 2, borderTop: '1px solid rgba(76, 175, 80, 0.2)', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Ore Lavoro</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{rapportino.ore_lavoro || '-'} h</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Tempo Viaggio</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{rapportino.tempo_viaggio_andata || 0} min</Typography>
                </Box>
                {intervento?.tipologia === 'manutenzione' && !rapportino.firma_cliente && (
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: '14px !important' }} />}
                    label="Da firmare"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 152, 0, 0.1)',
                      color: '#ff9800',
                      fontWeight: 600,
                      '& .MuiChip-icon': { color: '#ff9800' },
                    }}
                  />
                )}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {intervento?.tipologia === 'manutenzione' && !rapportino.firma_cliente && canCompilare && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DrawIcon />}
                      onClick={handleOpenFirmaDialog}
                      sx={{
                        borderRadius: 2,
                        bgcolor: '#ff9800',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#f57c00' },
                      }}
                    >
                      Raccogli Firma
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DescriptionIcon />}
                    onClick={() => generaRapportinoPDF(rapportino)}
                    sx={{ borderRadius: 2 }}
                  >
                    Stampa PDF
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleGestisciTimbrature}
                    sx={{ borderRadius: 2, borderColor: '#4caf50', color: '#4caf50' }}
                  >
                    Visualizza Dettagli
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {/* Tabs */}
        {canManage && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              sx={{
                bgcolor: 'grey.50',
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  minHeight: 56,
                  '&.Mui-selected': {
                    color: '#e50000',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#e50000',
                  height: 3,
                },
              }}
            >
              <Tab label="Dettagli Intervento" />
              <Tab label="Task Cantiere" />
            </Tabs>
          </Paper>
        )}

        {/* Tab Dettagli */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            {/* Informazioni Generali */}
            <Grid item xs={12}>
              <FormSection icon={<AssignmentIcon sx={{ fontSize: 20 }} />} title="Informazioni Generali">
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      name="tipologia"
                      label="Tipologia"
                      fullWidth
                      value={formData.tipologia}
                      onChange={handleInputChange}
                      disabled={!editing}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    >
                      <MenuItem value="manutenzione">Manutenzione</MenuItem>
                      <MenuItem value="impianto">Impianto</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      name="stato"
                      label="Stato"
                      fullWidth
                      value={formData.stato}
                      onChange={handleInputChange}
                      disabled={!editing}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    >
                      <MenuItem value="programmato">Programmato</MenuItem>
                      <MenuItem value="in_corso">In Corso</MenuItem>
                      <MenuItem value="completato">Completato</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="Data"
                      value={formData.data}
                      onChange={handleDateChange('data')}
                      disabled={!editing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.5}>
                    <TimePicker
                      label="Ora Inizio"
                      value={formData.ora_inizio}
                      onChange={handleDateChange('ora_inizio')}
                      disabled={!editing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.5}>
                    <TimePicker
                      label="Ora Fine"
                      value={formData.ora_fine}
                      onChange={handleDateChange('ora_fine')}
                      disabled={!editing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>

            {/* Cliente e Cantiere */}
            <Grid item xs={12} md={6}>
              <FormSection icon={<LocationIcon sx={{ fontSize: 20 }} />} title="Cliente e Cantiere">
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    {editing ? (
                      <Autocomplete
                        options={cantieri}
                        getOptionLabel={(option) => `${option.codice} - ${option.nome} (${option.citta})`}
                        value={cantieri.find((c) => c.id === formData.cantiere_id) || null}
                        onChange={handleCantiereChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Cantiere"
                            required
                            fullWidth
                            placeholder="Cerca cantiere..."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                      />
                    ) : (
                      <TextField
                        label="Cantiere"
                        fullWidth
                        value={intervento.cantiere || ''}
                        InputProps={{ readOnly: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Cliente"
                      fullWidth
                      value={
                        editing
                          ? clienti.find((c) => c.id === formData.cliente_id)?.ragione_sociale || ''
                          : intervento.cliente || ''
                      }
                      InputProps={{ readOnly: true }}
                      helperText={editing ? 'Compilato automaticamente in base al cantiere' : ''}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Indirizzo Cantiere"
                      fullWidth
                      value={intervento.cantiere_indirizzo || '-'}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>

            {/* Team */}
            <Grid item xs={12} md={6}>
              <FormSection icon={<GroupIcon sx={{ fontSize: 20 }} />} title="Team">
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    {editing ? (
                      <Autocomplete
                        options={capocantieri}
                        getOptionLabel={(option) => `${option.nome} ${option.cognome}`}
                        value={capocantieri.find((c) => c.id === formData.capocantiere_id) || null}
                        onChange={(event, newValue) => {
                          setFormData({
                            ...formData,
                            capocantiere_id: newValue ? newValue.id : '',
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Capocantiere"
                            required
                            fullWidth
                            placeholder="Cerca capocantiere..."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                      />
                    ) : (
                      <TextField
                        label="Capocantiere"
                        fullWidth
                        value={intervento.capocantiere || ''}
                        InputProps={{ readOnly: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {editing ? (
                      <Autocomplete
                        multiple
                        options={tecnici}
                        getOptionLabel={(option) => `${option.nome} ${option.cognome}`}
                        value={tecnici.filter((t) => formData.tecnici_ids.includes(t.id))}
                        onChange={(event, newValue) => {
                          setFormData({
                            ...formData,
                            tecnici_ids: newValue.map((t) => t.id),
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Tecnici"
                            fullWidth
                            placeholder="Cerca tecnici..."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                      />
                    ) : editingTecnici ? (
                      <Box>
                        {tecniciError && (
                          <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>
                            {tecniciError}
                          </Alert>
                        )}
                        <Autocomplete
                          multiple
                          options={tecnici}
                          getOptionLabel={(option) => `${option.nome} ${option.cognome}`}
                          value={tecnici.filter((t) => tecniciEdit.includes(t.id))}
                          onChange={(event, newValue) => setTecniciEdit(newValue.map((t) => t.id))}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tecnici"
                              fullWidth
                              autoFocus
                              placeholder="Cerca tecnici..."
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                          <Button size="small" onClick={handleCancelTecnici}>
                            Annulla
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveTecnici}
                            sx={{ bgcolor: '#e50000', '&:hover': { bgcolor: '#c50000' } }}
                          >
                            Salva tecnici
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <TextField
                        label="Tecnici"
                        fullWidth
                        value={
                          intervento.tecnici && intervento.tecnici.length > 0
                            ? intervento.tecnici.map((t) => t.nome).join(', ')
                            : 'Nessun tecnico assegnato'
                        }
                        InputProps={{
                          readOnly: true,
                          endAdornment: isCapoDiQuesto ? (
                            <Tooltip title="Modifica tecnici">
                              <IconButton edge="end" size="small" onClick={handleEditTecnici}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={veicoliDisponibili}
                      getOptionLabel={(option) => `${option.targa} - ${option.modello}`}
                      value={veicoliDisponibili.find(v => v.targa === formData.targa_mezzo) || null}
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          targa_mezzo: newValue ? newValue.targa : '',
                        });
                      }}
                      disabled={!editing}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Veicolo"
                          fullWidth
                          placeholder={veicoliDisponibili.length === 0 ? "Nessun veicolo disponibile" : "Seleziona veicolo..."}
                          helperText={
                            veicoliDisponibili.length === 0
                              ? "Nessun veicolo disponibile per questa data"
                              : `${veicoliDisponibili.length} veicolo/i disponibile/i`
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <CarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.targa === value?.targa}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.abilita_timbrature_viaggio}
                          onChange={(e) => setFormData(prev => ({ ...prev, abilita_timbrature_viaggio: e.target.checked }))}
                          disabled={!editing}
                          sx={{ color: '#e50000', '&.Mui-checked': { color: '#e50000' } }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2">Includi timbrature viaggio</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Disabilita se la squadra è già sul posto
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>

            {/* Dettagli Lavori */}
            <Grid item xs={12}>
              <FormSection icon={<NotesIcon sx={{ fontSize: 20 }} />} title="Dettagli Lavori">
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      name="descrizione_lavori"
                      label="Descrizione Lavori"
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.descrizione_lavori}
                      onChange={handleInputChange}
                      disabled={!editing}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="note_operative"
                      label="Note Operative"
                      fullWidth
                      multiline
                      rows={3}
                      value={formData.note_operative}
                      onChange={handleInputChange}
                      disabled={!editing}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>

            {/* Tempi di Viaggio */}
            {intervento.tempo_viaggio_andata && (
              <Grid item xs={12}>
                <FormSection icon={<TimerIcon sx={{ fontSize: 20 }} />} title="Tempi di Viaggio">
                  <Grid container spacing={3}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#e50000' }}>
                          {intervento.tempo_viaggio_andata}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">minuti andata</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#e50000' }}>
                          {intervento.tempo_viaggio_ritorno || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">minuti ritorno</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </FormSection>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Tab Task */}
        <TabPanel value={currentTab} index={1}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
                <AssignmentIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                Task del Cantiere: {intervento.cantiere}
              </Typography>
            </Box>

            {loadingTasks ? (
              <Box sx={{ py: 4 }}>
                <Skeleton variant="rounded" height={100} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={200} />
              </Box>
            ) : !Array.isArray(tasks) || tasks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <AssignmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Nessuna task associata a questo cantiere
                </Typography>
              </Box>
            ) : (
              <>
                {/* Statistiche Task */}
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.08)', borderRadius: 2, border: '1px solid rgba(33, 150, 243, 0.2)', textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>
                          {tasksStats.totali}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Totali</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.08)', borderRadius: 2, border: '1px solid rgba(76, 175, 80, 0.2)', textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                          {tasksStats.completate}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Completate</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.08)', borderRadius: 2, border: '1px solid rgba(255, 152, 0, 0.2)', textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                          {tasksStats.inCorso}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">In Corso</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Progress bar complessiva */}
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Progresso Complessivo</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#e50000' }}>{progressoTasks}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progressoTasks}
                      sx={{
                        height: 10,
                        borderRadius: 2,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: progressoTasks === 100 ? '#4caf50' : '#e50000',
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Tabella Task */}
                <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Descrizione</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Quantità</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Completate</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Progresso</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Stato</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasks.map((task) => {
                        const quantitaTotale = task.quantita || 0;
                        const quantitaCompletata = task.quantita_completata || 0;
                        const percentuale = quantitaTotale > 0
                          ? Math.round((quantitaCompletata / quantitaTotale) * 100)
                          : (task.completata ? 100 : 0);
                        const isCompleted = task.completata === true;

                        return (
                          <TableRow key={task.id} sx={{ '&:hover': { bgcolor: 'rgba(229, 0, 0, 0.02)' } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {isCompleted ? (
                                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                                ) : (
                                  <UncheckedIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                                )}
                                <Typography variant="body2">{task.descrizione}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={quantitaTotale || '-'} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={quantitaCompletata || 0}
                                size="small"
                                sx={{
                                  bgcolor: isCompleted ? 'rgba(76, 175, 80, 0.1)' : 'grey.100',
                                  color: isCompleted ? '#4caf50' : 'text.secondary',
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={percentuale}
                                  sx={{
                                    flex: 1,
                                    height: 8,
                                    borderRadius: 1,
                                    bgcolor: 'grey.200',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: isCompleted ? '#4caf50' : '#e50000',
                                    },
                                  }}
                                />
                                <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 35 }}>
                                  {percentuale}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={isCompleted ? 'Completata' : 'In Corso'}
                                size="small"
                                sx={{
                                  bgcolor: isCompleted ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                                  color: isCompleted ? '#4caf50' : '#ff9800',
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        </TabPanel>
      </Container>

      {/* FAB Duplica (mobile) — visibile solo in visualizzazione */}
      {isAdmin && isMobile && !editing && (
        <Fab
          size="medium"
          onClick={handleDuplicaIntervento}
          aria-label="Duplica intervento"
          sx={{
            position: 'fixed',
            bottom: 148,
            right: 16,
            bgcolor: 'white',
            color: '#e50000',
            border: '1px solid #e50000',
            '&:hover': { bgcolor: '#ffe9e9' },
          }}
        >
          <ContentCopyIcon />
        </Fab>
      )}

      {/* FAB mobile per modifica */}
      {isAdmin && isMobile && (
        <Fab
          color="primary"
          onClick={() => editing ? handleSave() : setEditing(true)}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            bgcolor: '#e50000',
            boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
            '&:hover': {
              bgcolor: '#c50000',
            },
          }}
        >
          {editing ? <SaveIcon /> : <EditIcon />}
        </Fab>
      )}

      {/* Dialog Firma Posticipata */}
      <Dialog
        open={firmaDialogOpen}
        onClose={handleCloseFirmaDialog}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            <DrawIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Raccogli Firma Cliente
          </Typography>
          {firmaError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFirmaError('')}>
              {firmaError}
            </Alert>
          )}
          <TextField
            label="Nome del Firmatario *"
            fullWidth
            value={firmaNome}
            onChange={(e) => setFirmaNome(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            placeholder="Nome e cognome di chi firma"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Firma con il dito: la tastiera si chiude automaticamente.
          </Typography>
          <Box
            onPointerDown={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); }}
            sx={{
              border: '2px solid #ddd',
              borderRadius: 1,
              p: 1,
              backgroundColor: 'white',
              overflowX: 'auto',
            }}
          >
            <SignatureCanvas
              ref={firmaSigCanvas}
              penColor="black"
              onBegin={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); }}
              canvasProps={{
                width: 500,
                height: 200,
                className: 'sigCanvas',
                style: { touchAction: 'none' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              onClick={() => firmaSigCanvas.current?.clear()}
              size="small"
            >
              Pulisci
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleCloseFirmaDialog}>
                Annulla
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveFirma}
                disabled={firmaLoading}
                sx={{
                  bgcolor: '#4caf50',
                  '&:hover': { bgcolor: '#388e3c' },
                  fontWeight: 600,
                }}
              >
                {firmaLoading ? 'Salvataggio...' : 'Salva Firma'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default InterventoDettaglio;
